<template>
    <vx-modal :visible="visible" @update:visible="updateVisible($event)" class="vx-max-w-lg" :padding="false">
        <template v-slot:header>
            <div class="vx-text-2xl vx-pt-6 vx-px-6" :class="{'vx-pb-6': !model.id}">
                {{ model.id ? 'Edit User' : 'Add User' }}
            </div>
        </template>

        <template v-if="model.id" v-slot:subheader>
            <div class="vx-px-6 vx-break-words" data-test="subheader">
                {{ model.name }} - {{ model.email }}
            </div>
        </template>

        <div class="vx-p-6">
            <div class="vx-flex vx-flex-col vx-gap-4">
                <vx-alert v-if="error" color="danger" :closeable="false">
                    {{ error }}
                </vx-alert>
                <div v-if="!model.id">
                    <vx-label>Email</vx-label>

                    <vx-selectable
                        :options="users"
                        :loading="searching"
                        relative-dropdown
                        :reduce="option => option.id"
                        @search="search"
                        v-model="userId"
                        data-test="email"
                        label="email"
                        size="lg"
                        :error="displayFirstError(validate.userId)">
                    </vx-selectable>
                </div>
                <div>
                    <vx-label>
                        Group Role
                        <template v-slot:description>View <a href="https://support.voxie.com/hc/en-us" target="_blank" rel="noopener noreferrer">permission chart</a> to see what each type has access to.</template>
                    </vx-label>

                    <vx-selectable v-model="model.role"
                        size="lg"
                        data-test="select-role"
                        relative-dropdown
                        :reduce="option => option.value"
                        :options="groupRolesOptions()"
                        :clearable="false"
                        :error="displayFirstError(validate.role)">
                    </vx-selectable>
                </div>
            </div>
        </div>

        <template v-slot:footer>
            <div class="vx-flex vx-flex-col-reverse vx-gap-4 lg:vx-flex-row">
                <vx-button @click="updateVisible(false)"
                    type="button"
                    class="vx-grow lg:vx-w-20 lg:vx-grow-0"
                    size="lg"
                    color="muted">
                    Cancel
                </vx-button>
                <vx-button @click.prevent="submit()"
                    :loading="submitting"
                    data-test="submit-btn"
                    type="button"
                    class="vx-grow"
                    size="lg"
                    color="primary">
                    {{ model.id ? 'Update' : 'Add' }}
                    <font-awesome-icon :icon="model.id ? faCircleCheck : faPlusCircle"></font-awesome-icon>
                </vx-button>
            </div>
        </template>
    </vx-modal>
</template>
<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { VxAlert, VxButton, VxLabel, VxModal, VxSelectable } from '@voxie/frontend-components';
import { groupUsersClient } from '~/services';
import { HTTP_INTERNAL_SERVER_ERROR, HTTP_OK, HTTP_CREATED } from '~/support/HttpStatuses';
import { faCircleCheck, faPlusCircle } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { groupRolesOptions } from '~/constants/roles';
import { displayFirstError } from '~/utils/validation';
import useVuelidate from '@vuelidate/core';
import { required, requiredIf } from '@vuelidate/validators';
import debounce from '~/utils/debounce';
import usersClient from '~/services/usersClient';

const visible = defineModel('visible', {
    type: Boolean,
    default: false,
});

const model = defineModel({
    type: Object,
    required: true,
});

const props = defineProps({
    groupId: {
        type: String,
        required: true,
    }
});

const error = ref('');
const userId = ref(null);

const validate = useVuelidate(
    {
        userId: {
            requiredIf: requiredIf(() => model.value.id === null),
        },
        role: {
            required: required,
        }
    },
    computed(() => ({ role: model.value.role, userId: userId.value})),
    {
        $autoDirty: true,
    }
)

const emit = defineEmits(['added', 'updated']);

const teamId = Spark.state.currentTeam.id;
const submitting = ref(false);

const users = ref([]);
const searching = ref(false);
const search = debounce((search) => {
    searching.value = true;
    usersClient.listUsers(teamId, {
        query: search
    }).then((response) => {
        users.value = response.data.data;
        searching.value = false;
    })
}, 200);

onMounted(() => {
    if (!model.value.id) {
        search();
    }
});

watch(userId, (selectedUserId) => {
    if (null !== selectedUserId) {
        const selectedUser = users.value.find((user) => user.id === selectedUserId);

        if (selectedUser) {
            model.value.name = selectedUser.name;
            model.value.email = selectedUser.email;
        }
    }
});

const updateVisible = (isVisible) => {
    if (!model.value.id) {
        userId.value = null;
    }

    validate.value.$reset();
    error.value = '';
    visible.value = isVisible;
}

const submit = async () => {
    const isValid = await validate.value.$validate();

    if (!isValid || submitting.value) {
        return;
    }

    error.value = '';
    submitting.value = true;

    try {
        const response = await groupUsersClient.upsertGroupUser(teamId, props.groupId, model.value.id || userId.value, { role: model.value.role });

        if (response.status === HTTP_CREATED) {
            emit('added', { ...model.value, id: userId.value });
        } else if (response.status === HTTP_OK) {
            emit('updated', model.value.id || userId.value);
        }

        updateVisible(false);
    } catch (e) {
        console.error(e);
        if (!e?.response?.status || e.response.status >= HTTP_INTERNAL_SERVER_ERROR) {
            error.value = 'Something went wrong.'
        } else {
            error.value = e.response?.data?.message || 'Something went wrong.';
        }
    } finally {
        submitting.value = false;
    }
}
</script>
